import { api, ApiError, type InviteInfo } from "../api.ts";
import { esc, h, setMsg } from "../dom.ts";
import { t } from "../i18n/index.ts";
import { navigate, type Params } from "../router.ts";
import { currentUser, refreshUser } from "../session.ts";
import { brandLockup, BRAND_NAME } from "../ui/brand.ts";
import { rememberReturn } from "../returnContext.ts";

/** Onboarding for an invite link (`/i/:token`): one screen — pick a username +
 *  password and play (UC-1). The signed link at the invited inbox *is* the email
 *  proof, so there is no code step. */
export async function onboardScreen(params: Params): Promise<HTMLElement> {
  const token = params.token ?? "";

  let info;
  try {
    info = await api.resolveInvite(token);
  } catch (err) {
    return notice(
      t("onboard.notFoundTitle"),
      err instanceof ApiError ? err.message : t("onboard.brokenLink"),
    );
  }

  const user = currentUser();
  if (!user) rememberReturn(`/i/${token}`);

  if (!info.usable) {
    if (info.status === "accepted" && info.game_id) {
      // Already a game. A signed-in member goes straight into it; otherwise
      // we can't know whose game it is, so send them to sign in.
      if (user) {
        navigate(`/game/${info.game_id}`);
        return h(`<main class="card"><p class="msg">${t("common.openingGame")}</p></main>`);
      }
      return notice(
        t("onboard.acceptedTitle"),
        t("onboard.acceptedBody"),
        "/login",
        t("login.signIn"),
      );
    }
    if (info.expired) {
      return notice(t("onboard.expiredTitle"), t("onboard.expiredBody"));
    }
    return notice(t("onboard.unavailTitle"), t("onboard.unavailBody"));
  }

  // A signed-in member doesn't need the email→code→signup wizard; the server
  // accepts the challenge directly (plan §7.1: "already a member → accept").
  if (user) {
    return acceptView(token, info, user.username);
  }

  const config = await api.getConfig().catch(() => null);
  if (config?.registration_mode !== "private_invite") {
    return notice(t("email.inviteTitle"), t("email.inviteBody"), "/login", t("login.signIn"));
  }

  // The invited inbox already has an account, but this browser isn't signed in.
  // Don't make them "create" a duplicate — just welcome them back for a one-field
  // sign-in (their username is known), with a reset link in case they forgot.
  if (info.member && info.member_username) {
    return memberSignInView(token, info, info.member_username);
  }

  const challenger = info.challenger ?? t("onboard.aFriend");
  const email = info.email ?? "";

  const el = h(`
    <main class="card">
      <h1 class="brand" aria-label="${BRAND_NAME}">${brandLockup}</h1>
      <p class="challenge">${t("onboard.headline", {
        who: `<strong>${esc(challenger)}</strong>`,
        tc: `<strong>${esc(info.tc)}</strong>`,
        kind: info.rated ? t("chess.rated") : t("chess.casual"),
      })}</p>
      <p class="fine">${t("onboard.signupIntro", { email: `<strong>${esc(email)}</strong>` })}</p>

      <form id="signup" novalidate>
        <label class="field"><span>${t("onboard.username")}</span>
          <input id="username" autocomplete="username" autofocus /></label>
        <label class="field"><span>${t("onboard.password")}</span>
          <input id="password" type="password" autocomplete="new-password" /></label>
        <button class="btn primary" type="submit">${t("onboard.createPlay")}</button>
        <p class="msg" role="status" aria-live="polite"></p>
      </form>
    </main>
  `);

  const msg = el.querySelector(".msg") as HTMLElement;
  const form = el.querySelector<HTMLFormElement>("#signup")!;
  const button = form.querySelector("button") as HTMLButtonElement;

  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    const username = el.querySelector<HTMLInputElement>("#username")!.value.trim();
    const password = el.querySelector<HTMLInputElement>("#password")!.value;
    if (username.length < 3) {
      setMsg(msg, t("onboard.needUser"), "bad");
      return;
    }
    if (password.length < 8) {
      setMsg(msg, t("onboard.needPass"), "bad");
      return;
    }
    button.disabled = true;
    setMsg(msg, t("onboard.creating"));
    try {
      const res = await api.register(token, username, password);
      await refreshUser();
      navigate(`/game/${res.game_id}`);
    } catch (err) {
      setMsg(msg, err instanceof ApiError ? err.message : t("common.somethingWrong"), "bad");
      button.disabled = false;
    }
  });

  return el;
}

/** One-click accept for an already-signed-in member. */
function acceptView(token: string, info: InviteInfo, username: string): HTMLElement {
  const challenger = info.challenger ?? t("onboard.aFriend");

  const el = h(`
    <main class="card">
      <h1 class="brand" aria-label="${BRAND_NAME}">${brandLockup}</h1>
      <p class="challenge">${t("onboard.headline", {
        who: `<strong>${esc(challenger)}</strong>`,
        tc: `<strong>${esc(info.tc)}</strong>`,
        kind: info.rated ? t("chess.rated") : t("chess.casual"),
      })}</p>
      <p class="fine">${t("onboard.signedInAs", { who: `<strong>${esc(username)}</strong>` })}</p>
      <div class="accept-actions">
        <button class="btn primary" id="accept" type="button">${t("onboard.acceptPlay")}</button>
        <button class="btn ghost" id="decline" type="button">${t("onboard.decline")}</button>
      </div>
      <p class="fine"><a data-link href="/">${t("onboard.notNow")}</a></p>
      <p class="msg" role="status" aria-live="polite"></p>
    </main>
  `);

  const msg = el.querySelector(".msg") as HTMLElement;
  const acceptBtn = el.querySelector<HTMLButtonElement>("#accept")!;
  const declineBtn = el.querySelector<HTMLButtonElement>("#decline")!;
  acceptBtn.addEventListener("click", async () => {
    acceptBtn.disabled = true;
    declineBtn.disabled = true;
    setMsg(msg, t("onboard.accepting"));
    try {
      const res = await api.acceptInvite(token);
      navigate(`/game/${res.game_id}`);
    } catch (err) {
      setMsg(msg, err instanceof ApiError ? err.message : t("onboard.cantAccept"), "bad");
      acceptBtn.disabled = false;
      declineBtn.disabled = false;
    }
  });
  declineBtn.addEventListener("click", async () => {
    acceptBtn.disabled = true;
    declineBtn.disabled = true;
    setMsg(msg, t("onboard.declining"));
    try {
      await api.declineInvite(token);
      setMsg(msg, t("onboard.declined", { who: challenger }), "ok");
      window.setTimeout(() => navigate("/"), 1200);
    } catch (err) {
      setMsg(msg, err instanceof ApiError ? err.message : t("onboard.cantDecline"), "bad");
      acceptBtn.disabled = false;
      declineBtn.disabled = false;
    }
  });

  return el;
}

/** Returning member (their email already has an account) accepting from a fresh
 *  browser: greet them, ask only for the password, then sign in + accept. */
function memberSignInView(token: string, info: InviteInfo, username: string): HTMLElement {
  const challenger = info.challenger ?? t("onboard.aFriend");

  const el = h(`
    <main class="card">
      <h1 class="brand" aria-label="${BRAND_NAME}">${brandLockup}</h1>
      <p class="challenge">${t("onboard.headline", {
        who: `<strong>${esc(challenger)}</strong>`,
        tc: `<strong>${esc(info.tc)}</strong>`,
        kind: info.rated ? t("chess.rated") : t("chess.casual"),
      })}</p>
      <p class="fine">${t("onboard.welcomeBack", { who: `<strong>${esc(username)}</strong>` })}</p>

      <form id="signin" novalidate>
        <input type="hidden" name="username" autocomplete="username" value="${esc(username)}" />
        <label class="field"><span>${t("onboard.password")}</span>
          <input id="password" type="password" autocomplete="current-password" autofocus /></label>
        <button class="btn primary" type="submit">${t("onboard.signinPlay")}</button>
        <p class="msg" role="status" aria-live="polite"></p>
      </form>
      <p class="fine"><a data-link href="/forgot">${t("login.forgot")}</a></p>
    </main>
  `);

  const msg = el.querySelector(".msg") as HTMLElement;
  const form = el.querySelector<HTMLFormElement>("#signin")!;
  const button = form.querySelector("button") as HTMLButtonElement;

  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    const password = el.querySelector<HTMLInputElement>("#password")!.value;
    if (!password) {
      setMsg(msg, t("onboard.needPassword"), "bad");
      return;
    }
    button.disabled = true;
    setMsg(msg, t("onboard.signingYouIn"));
    try {
      await api.login(username, password);
      await refreshUser();
      const res = await api.acceptInvite(token);
      navigate(`/game/${res.game_id}`);
    } catch (err) {
      setMsg(msg, err instanceof ApiError ? err.message : t("login.couldNot"), "bad");
      button.disabled = false;
    }
  });

  return el;
}

function notice(title: string, body: string, href = "/", link = t("common.home")): HTMLElement {
  return h(`
    <main class="card">
      <h1 class="brand" aria-label="${BRAND_NAME}">${brandLockup}</h1>
      <h2>${esc(title)}</h2>
      <p class="tagline">${esc(body)}</p>
      <p><a class="btn ghost" data-link href="${esc(href)}">${esc(link)}</a></p>
    </main>
  `);
}
